/**
 * PATH: packages/ui/src/kanban/components/kanbanCardVisuals.ts
 * FILE_ID: UI-KANBAN-CARD-VISUALS-001
 * PURPOSE: Pure, deterministic visual helpers for the Kanban card face —
 *   the accent palette + hash, per-card accent pick, date chip format, and
 *   mailbox/sender initials.
 * WHY: Extracted from the 1.3k-line KanbanCard.tsx so the card stays a thin
 *   orchestrator (≤500-line working limit, 12_engineering_standards §3). These
 *   are I→O functions shared by the card root, the email-meta row and the
 *   footer chrome — one home keeps the palette/hash identical across all three.
 * FLOW:
 *   ┌──────────────┐   ┌──────────────────┐   ┌──────────────┐
 *   │ card / string │ → │ hash % palette / │ → │ colour /     │
 *   │               │   │ Intl format      │   │ label string │
 *   └──────────────┘   └──────────────────┘   └──────────────┘
 * RELATED:
 *   - ./KanbanCard.tsx (root accent border).
 *   - ./KanbanCardEmailMeta.tsx (sender avatar colour).
 *   - ./KanbanCardFooter.tsx (mailbox chip colour).
 */
import type { Card as KanbanCardModel } from '../types'

/** Stable per-key colour from a small Trello-friendly palette. Used for
 * sender avatars and mailbox chips, where a stable visual identity helps
 * distinguish repeated values without turning the card surface into a status
 * signal. */
export const CARD_ACCENT_PALETTE = [
  '#EF4444', // red
  '#F97316', // orange
  '#F59E0B', // amber
  '#EAB308', // yellow
  '#84CC16', // lime
  '#22C55E', // green
  '#10B981', // emerald
  '#14B8A6', // teal
  '#06B6D4', // cyan
  '#0EA5E9', // sky
  '#3B82F6', // blue
  '#6366F1', // indigo
  '#8B5CF6', // violet
  '#A855F7', // purple
  '#D946EF', // fuchsia
  '#EC4899', // pink
] as const

/** djb2-ish string hash. Fast, deterministic, no deps. */
export function hashString(s: string): number {
  let h = 5381
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0
  return Math.abs(h)
}

/** Return an operator-assigned tag colour for the card edge, if any.
 * Generic job/card hashes deliberately do not paint the card chrome: random
 * identity colours look like selection, priority, or error states. */
export function pickCardAccent(card: KanbanCardModel): string | undefined {
  const firstTagColor = card.tags?.[0]?.tag?.color
  return firstTagColor || undefined
}

/** Hash any address/name to a stable palette colour (sender avatar,
 *  mailbox chip). Prefix the seed so the same string used for two
 *  different roles still yields independent hues. */
export function pickHashedColor(seed: string): string {
  return CARD_ACCENT_PALETTE[hashString(seed) % CARD_ACCENT_PALETTE.length]
}

/** Format an ISO date as "1 May" for the card-face date chip.
 *  Returns the raw input if parsing fails, so we never crash on
 *  a malformed timestamp from the backend. */
export function formatCardDate(iso: string): string {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
  } catch {
    return iso
  }
}

/** Human-readable tooltip/aria-label for the card's NEXT-ACTION (due)
 *  date pill. The pill itself only paints a bare "MM-DD" to stay compact,
 *  which reads as an anonymous number on a triage card — this spells out
 *  what that number MEANS so the operator (or a screen reader) knows it's
 *  the next-action deadline, and whether it has already passed. Operator
 *  ask 2026-06-14: "show the date on the card for the next action".
 *  Pure: f(iso, overdue) → label string. */
export function formatDuePillTitle(dueIso: string, overdue: boolean): string {
  const pretty = formatCardDate(dueIso)
  return overdue ? `Overdue — was due ${pretty}` : `Next action due ${pretty}`
}

/** Human-readable tooltip/aria-label for the card's ACTION-date pill
 *  (FEAT-2026-06-29-KANBAN-ACTION-DATE) — the operator's PRIMARY,
 *  hand-authored "next action" date. When the short action note is present
 *  (drawer payload) it's appended so the hover spells out the next move;
 *  on the lean board tile the note is absent and the label is just the date.
 *  Pure: f(iso, note?) → label string. */
export function formatActionPillTitle(actionIso: string, note?: string | null): string {
  const pretty = formatCardDate(actionIso)
  const trimmed = (note ?? '').trim()
  return trimmed ? `Action ${pretty} — ${trimmed}` : `Action ${pretty}`
}

/** Human-readable tooltip/aria-label for the snooze pill. Mirrors
 *  formatDuePillTitle so the two date pills speak the same language.
 *  Pure: f(iso) → label string. */
export function formatSnoozePillTitle(snoozedUntilIso: string): string {
  return `Snoozed until ${formatCardDate(snoozedUntilIso)}`
}

/**
 * FEAT-2026-06-30-KANBAN-PRIORITY-FLAG — colour + label for a card-face
 * priority flag. Operator (2026-06-30): *"the priority is not very clear, can we
 * colour the priority flag with the colour of priority?"*. The left-edge bar
 * (cardPriorityBorder) was the only signal and read as decoration; a tinted,
 * filled flag with a spelled-out label/tooltip makes priority explicit.
 *
 * Colours mirror `cardPriorityBorder` and the right-click priority submenu so
 * the edge bar, the flag and the menu all stay in lock-step:
 *   3 = Urgent → red-500   (#ef4444)
 *   2 = High   → amber-500 (#f59e0b)
 *   1 = Normal → blue-500  (#3b82f6)
 *   0 = Low → null (no flag — the calm default, so a flag always MEANS a set
 *       priority).
 * Pure: f(priority) → {color,label} | null.
 */
export function priorityFlagMeta(
  priority: number,
): { color: string; label: string } | null {
  if (priority >= 3) return { color: '#ef4444', label: 'Urgent' }
  if (priority === 2) return { color: '#f59e0b', label: 'High' }
  if (priority === 1) return { color: '#3b82f6', label: 'Normal' }
  return null
}

/**
 * Card design v2 (canvas kanban-card-design-v2, operator 2026-07-07) — the
 * ONE reference chip on the card face: which business record this card is
 * anchored to. Exactly one renders (precedence below); the full linked-record
 * set stays in the drawer. More than one chip = the variant-C rainbow the
 * design explicitly rejects.
 *
 * Precedence: job (the operator's primary axis; human-readable id) →
 * quote (human quote_number when the crm_pipeline mirrored it, generic
 * otherwise) → invoice (UUID-only on the wire, so generic label).
 * Pure: f(card) → {label,title} | null.
 */
export function cardReferenceChip(
  card: KanbanCardModel,
): { label: string; title: string } | null {
  if (card.job_id != null) {
    return {
      label: `Job ${card.job_id}`,
      title: `Linked to job ${card.job_id} — open the card for details`,
    }
  }
  const quoteNo = card.quote_meta_json?.quote_number
  if (quoteNo != null && `${quoteNo}`.trim() !== '') {
    return {
      label: `Q-${quoteNo}`,
      title: `Linked to quote ${quoteNo} — open the card for details`,
    }
  }
  if (card.quote_id) {
    return { label: 'Quote', title: 'Linked to a quote — open the card for details' }
  }
  if (card.invoice_id) {
    return { label: 'Invoice', title: 'Linked to an invoice — open the card for details' }
  }
  return null
}

/** Two-letter initials from a name or email address. Falls back to
 *  the first two characters of the local-part for an email. Used by
 *  the mailbox dot/avatar. */
export function getMailboxInitials(label: string): string {
  if (!label) return '??'
  const local = label.includes('@') ? label.split('@')[0] : label
  const parts = local.split(/[.\s_-]+/).filter(Boolean)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return local.slice(0, 2).toUpperCase()
}
